import { useState, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { defaultProfileImage } from '../constants/defaultProfileImage';
import { BELT_RANKS, getBeltForSessions } from '../constants/beltRanks';
import { db, requestNotificationPermission } from '../firebase';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc, increment, setDoc, arrayUnion } from 'firebase/firestore';
import { useFriends } from '../hooks/useFriends';
import { getCroppedImg } from '../utils/cropImage';
import { playButtonClick } from '../utils/audio';

export default function ProfilePage({ user, darkMode, profile, onBack, onDeleteProfile, onBackToMain }) {
  const [sessions, setSessions] = useState({});
  const [sessionCount, setSessionCount] = useState(0);
  const [workoutCount, setWorkoutCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [editingSession, setEditingSession] = useState(null);
  const [editWorkouts, setEditWorkouts] = useState([]);
  const [editIntensities, setEditIntensities] = useState({});
  const [profileImageLocal, setProfileImageLocal] = useState(null);
  const fileInputRef = useRef(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [showDeleteSessionModal, setShowDeleteSessionModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [beltRank, setBeltRank] = useState(getBeltForSessions(0));
  const [showRanksModal, setShowRanksModal] = useState(false);
  const [showRemoveFriendModal, setShowRemoveFriendModal] = useState(false);
  const [removeFriendError, setRemoveFriendError] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [loadingNotifPref, setLoadingNotifPref] = useState(false);
  const [sendingTestNotif, setSendingTestNotif] = useState(false);
  const [isDeletingProfile, setIsDeletingProfile] = useState(false);

  const { removeFriend } = useFriends(user || {});

  const isOwnProfile = !profile;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setLoadingSessions(true);
      const targetUid = profile?.uid || user?.uid;
      if (!targetUid) return;

      try {
        // Load sessions
        const sessionsSnap = await getDocs(collection(db, 'users', targetUid, 'sessions'));
        const sessionData = {};
        sessionsSnap.forEach((doc) => {
          sessionData[doc.id] = doc.data();
        });
        setSessions(sessionData);

        // Load profile to get sessionCount
        const profileRef = doc(db, 'profiles', targetUid);
        const profileSnap = await getDoc(profileRef);
        const profileData = profileSnap.data() || {};
        const sessions = profileData.sessionCount || 0;
        setSessionCount(sessions);
        const computedBelt = getBeltForSessions(sessions);
        
        // Use stored beltRank if it exists, otherwise compute it
        const storedBeltName = profileData.beltRank || computedBelt.name;
        const storedBelt = BELT_RANKS.find(b => b.name === storedBeltName) || computedBelt;
        
        // Trust the database beltRank field - it's updated when logging sessions
        setBeltRank(storedBelt);
        
        // Sync database if stored belt doesn't match computed (data integrity)
        if (isOwnProfile && storedBelt.name !== computedBelt.name) {
          await updateDoc(profileRef, { beltRank: computedBelt.name });
          setBeltRank(computedBelt);
        }
      } catch (err) {
        console.error('Failed to load data', err);
        // Fallback to localStorage for own profile
        if (!profile) {
          const sessionData = JSON.parse(localStorage.getItem('fitBudly_sessions') || '{}');
          setSessions(sessionData);
          setSessionCount(0);
        }
      } finally {
        setLoading(false);
        setLoadingSessions(false);
        // Minimum loading time for smoother UX
        setTimeout(() => setInitialLoading(false), 800);
      }
    };
    loadData();
  }, [profile, user]);

  // Load notification preference for this friend
  useEffect(() => {
    const loadNotificationPref = async () => {
      if (!profile?.uid || !user?.uid) return; // Only for viewing friends
      
      try {
        const prefRef = doc(db, 'notificationPreferences', `${user.uid}_${profile.uid}`);
        const prefSnap = await getDoc(prefRef);
        
        if (prefSnap.exists()) {
          setNotificationsEnabled(prefSnap.data().enabled || false);
        }
      } catch (err) {
        console.error('Failed to load notification preference', err);
      }
    };
    
    loadNotificationPref();
  }, [profile, user]);

  useEffect(() => {
    const loadProfileStats = async () => {
      const targetUid = profile?.uid || user?.uid;
      if (!targetUid) return;
      try {
        const profileRef = doc(db, 'profiles', targetUid);
        const profileSnap = await getDoc(profileRef);
        const profileData = profileSnap.data() || {};
        setWorkoutCount(profileData.workoutCount || 0);
      } catch (err) {
        console.error('Failed to load workout count', err);
      }
    };
    loadProfileStats();
  }, [profile, user]);

  const displayUser = profile || { username: user?.username, profileImage: user?.profileImage };
  const profileSessions = sessionCount;
  const profileWorkouts = workoutCount;

  const getBeltGradientText = (beltColor, fontSize) => ({
    fontSize,
    fontWeight: 'bold',
    backgroundImage: `linear-gradient(to bottom, #ffffff 0%, #ffffff 5%, ${beltColor} 100%)`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: '#ffffff',
    display: 'inline-block',
    filter: 'drop-shadow(-1px -1px 0 #000) drop-shadow(1px -1px 0 #000) drop-shadow(-1px 1px 0 #000) drop-shadow(1px 1px 0 #000)'
  });
  const username = displayUser?.username || 'Athlete';
  const profileImageSrc = profileImageLocal || displayUser?.profileImage || defaultProfileImage;

  const Spinner = ({ color }) => (
    <span style={{
      display: 'inline-block',
      width: '24px',
      height: '24px',
      border: `3px solid ${color}`,
      borderTopColor: 'transparent',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      WebkitAnimation: 'spin 0.8s linear infinite'
    }} />
  );

  const startEditSession = (date) => {
    const session = sessions[date];
    const currentWorkouts = (session?.workouts || []).map(w => ({ name: w.name, intensity: w.intensity }));
    setEditingSession({ date, original: session });
    setEditWorkouts(currentWorkouts.map(w => w.name));
    const intensityMap = {};
    currentWorkouts.forEach(w => { intensityMap[w.name] = w.intensity; });
    setEditIntensities(intensityMap);
    setShowEditModal(true);
  };

  const toggleEditWorkout = (name) => {
    setEditWorkouts(prev => {
      if (prev.includes(name)) {
        return prev.filter(n => n !== name);
      } else {
        // Add workout with default intensity from original or 5
        const original = editingSession?.original?.workouts || [];
        const originalWorkout = original.find(w => w.name === name);
        setEditIntensities(prev => ({ ...prev, [name]: originalWorkout?.intensity || 5 }));
        return [...prev, name];
      }
    });
  };

  const hasChanges = () => {
    if (!editingSession?.original) return false;
    const original = editingSession.original.workouts || [];
    
    // Check if workout list changed
    if (editWorkouts.length !== original.length) return true;
    
    // Check if any workout was added/removed
    const originalNames = original.map(w => w.name).sort();
    const currentNames = [...editWorkouts].sort();
    if (JSON.stringify(originalNames) !== JSON.stringify(currentNames)) return true;
    
    // Check if any intensity changed
    for (const workout of original) {
      if (editIntensities[workout.name] !== workout.intensity) return true;
    }
    
    return false;
  };

  const saveSessionEdits = async () => {
    if (!isOwnProfile) { setShowEditModal(false); return; }
    if (!user?.uid || !editingSession?.date) return;
    const confirmEdit = window.confirm('Are you sure you want to edit?');
    if (!confirmEdit) return;

    const { date, original } = editingSession;
    const newWorkouts = editWorkouts.map(name => ({ name, intensity: editIntensities[name] || 5 }));
    const delta = (newWorkouts.length - (original?.workouts?.length || 0));

    try {
      const sessionRef = doc(db, 'users', user.uid, 'sessions', date);
      await updateDoc(sessionRef, {
        workouts: newWorkouts,
        totalWorkouts: newWorkouts.length
      });
      const profileRef = doc(db, 'profiles', user.uid);
      if (delta !== 0) {
        await updateDoc(profileRef, { workoutCount: increment(delta) });
        setWorkoutCount(prev => prev + delta);
      }
      setSessions(prev => ({ ...prev, [date]: { ...prev[date], workouts: newWorkouts, totalWorkouts: newWorkouts.length } }));
      setShowEditModal(false);
      setEditingSession(null);
    } catch (err) {
      console.error('Failed to edit session', err);
      alert('Failed to edit session: ' + err.message);
    }
  };

  const deleteSession = async (date) => {
    if (!isOwnProfile) return;
    setSessionToDelete(date);
    setShowDeleteSessionModal(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;
    const session = sessions[sessionToDelete];
    try {
      const sessionRef = doc(db, 'users', user.uid, 'sessions', sessionToDelete);
      await deleteDoc(sessionRef);
      const count = session?.workouts?.length || 0;
      const profileRef = doc(db, 'profiles', user.uid);
      const newSessionCount = Math.max(0, sessionCount - 1);
      const newBelt = getBeltForSessions(newSessionCount);
      await updateDoc(profileRef, {
        sessionCount: increment(-1),
        workoutCount: increment(-count),
        beltRank: newBelt.name
      });
      setSessionCount(newSessionCount);
      setBeltRank(newBelt);
      setWorkoutCount(prev => Math.max(0, prev - count));
      setSessions(prev => {
        const copy = { ...prev };
        delete copy[sessionToDelete];
        return copy;
      });
      setShowDeleteSessionModal(false);
      setSessionToDelete(null);
    } catch (err) {
      console.error('Failed to delete session', err);
      alert('Failed to delete session: ' + err.message);
    }
  };

  const compressDataUrl = (dataUrl) => new Promise((resolve) => {
    if (dataUrl.length <= 200000) {
      resolve(dataUrl);
      return;
    }
    const img = new Image();
    img.onload = () => {
      const maxDim = 200;
      let { width, height } = img;
      const scale = Math.min(maxDim / width, maxDim / height, 1);
      const targetW = Math.max(1, Math.round(width * scale));
      const targetH = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetW, targetH);
      let q = 0.85;
      let compressed = canvas.toDataURL('image/jpeg', q);
      while (compressed.length > 200000 && q > 0.5) {
        q -= 0.05;
        compressed = canvas.toDataURL('image/jpeg', q);
      }
      resolve(compressed);
    };
    img.src = dataUrl;
  });

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.uid) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      setCropImageSrc(dataUrl);
      setShowCropModal(true);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const applyCrop = async () => {
    if (!cropImageSrc || !croppedAreaPixels || !user?.uid) return;
    setUploadingImage(true);
    try {
      const cropped = await getCroppedImg(cropImageSrc, croppedAreaPixels, 200);
      const dataUrl = await compressDataUrl(cropped);
      const profileRef = doc(db, 'profiles', user.uid);
      await updateDoc(profileRef, { profileImage: dataUrl });
      setProfileImageLocal(dataUrl);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setShowCropModal(false);
    } catch (err) {
      console.error('Failed to update profile image', err);
      alert('Failed to update profile image: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const toggleNotifications = async () => {
    if (!profile?.uid || !user?.uid) return;

    setLoadingNotifPref(true);
    try {
      const newState = !notificationsEnabled;
      const userProfileRef = doc(db, 'profiles', user.uid);

      // Always refresh token when enabling (tokens are per-browser, not per-user)
      if (newState) {
        const token = await requestNotificationPermission();
        await updateDoc(userProfileRef, {
          fcmToken: token,
          fcmTokens: arrayUnion(token),
          fcmTokenUpdatedAt: new Date().toISOString()
        });
      }

      const prefRef = doc(db, 'notificationPreferences', `${user.uid}_${profile.uid}`);
      await setDoc(prefRef, {
        userId: user.uid,
        friendId: profile.uid,
        enabled: newState,
        updatedAt: new Date().toISOString()
      });

      setNotificationsEnabled(newState);
    } catch (err) {
      console.error('Failed to toggle notifications', err);
      alert(err.message || 'Failed to update notification settings');
    } finally {
      setLoadingNotifPref(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (deleteConfirmation.toLowerCase() !== 'delete profile') {
      setDeleteError('Please type "delete profile" to confirm');
      return;
    }
    try {
      setDeleteError('');
      setIsDeletingProfile(true);
      await onDeleteProfile();
      setDeleteConfirmation('');
      setShowDeleteModal(false);
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete profile');
    } finally {
      setIsDeletingProfile(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'transparent',
      padding: '16px',
      color: darkMode ? '#f9fafb' : '#111827'
    }}>
      {/* Loading Animation */}
      {initialLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeOut 0.5s ease-out forwards',
          animationDelay: '0.5s'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #374151',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            marginTop: '24px',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#3b82f6',
            textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
          }}>
            Loading Profile...
          </p>
        </div>
      )}

      {/* Deleting Profile Overlay */}
      {isDeletingProfile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #374151',
            borderTop: '4px solid #ef4444',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            marginTop: '24px',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#ef4444',
            textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
          }}>
            Deleting Profile...
          </p>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeOut {
          to {
            opacity: 0;
            visibility: hidden;
          }
        }
        @-webkit-keyframes spin {
          0% { -webkit-transform: rotate(0deg); }
          100% { -webkit-transform: rotate(360deg); }
        }
      `}</style>
      <div style={{ maxWidth: '420px', margin: '0 auto', marginTop: '32px', textAlign: 'center' }}>
        <button
          onClick={() => { playButtonClick(); onBack(); }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: '#ffffff',
            fontWeight: 'bold',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            marginBottom: '32px',
            fontSize: '16px',
            textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
          }}
        >
          ← Back
        </button>

        {/* Profile Header */}
        <div style={{
          backgroundColor: darkMode ? '#1f2937' : '#ffffff',
          backgroundImage: darkMode
            ? `linear-gradient(135deg, ${beltRank.color}33, rgba(15,23,42,0.8)), radial-gradient(circle at 20% 20%, ${beltRank.color}33, transparent 55%), radial-gradient(circle at 80% 10%, ${beltRank.color}2B, transparent 55%)`
            : `linear-gradient(135deg, ${beltRank.color}26, rgba(255,255,255,0.9)), radial-gradient(circle at 20% 20%, ${beltRank.color}1F, transparent 55%), radial-gradient(circle at 80% 10%, ${beltRank.color}1A, transparent 55%)`,
          padding: '32px',
          borderRadius: '8px',
          border: '2px solid ' + (darkMode ? '#374151' : '#e5e7eb'),
          textAlign: 'center',
          marginBottom: '32px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 0 0 2px rgba(0,0,0,0.35), 0 12px 30px rgba(0,0,0,0.25)'
        }}>
          {/* Anime-style background accents */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(120deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.0) 35%, rgba(255,255,255,0.06) 70%, rgba(255,255,255,0.0) 100%)',
            opacity: darkMode ? 0.6 : 0.4,
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            top: '-60px',
            right: '-80px',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${beltRank.color}59 0%, ${beltRank.color}00 70%)`,
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-70px',
            left: '-60px',
            width: '220px',
            height: '220px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${beltRank.color}47 0%, ${beltRank.color}00 70%)`,
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            top: '18px',
            left: '-40px',
            width: '180px',
            height: '6px',
            background: 'linear-gradient(90deg, rgba(255,255,255,0.0), rgba(255,255,255,0.35), rgba(255,255,255,0.0))',
            transform: 'rotate(-12deg)',
            opacity: 0.6,
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '26px',
            right: '-30px',
            width: '180px',
            height: '6px',
            background: 'linear-gradient(90deg, rgba(255,255,255,0.0), rgba(255,255,255,0.35), rgba(255,255,255,0.0))',
            transform: 'rotate(12deg)',
            opacity: 0.6,
            pointerEvents: 'none'
          }} />
          {isOwnProfile && (
            <p style={{
              fontSize: '24px',
              fontWeight: '900',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: '#f9fafb',
              marginBottom: '12px',
              textShadow: '-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000, 0 0 12px rgba(59,130,246,0.6)'
            }}>
              My Profile
            </p>
          )}
          {!isOwnProfile && (
            <p style={{
              fontSize: '24px',
              fontWeight: '900',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: '#f9fafb',
              marginBottom: '12px',
              textShadow: '-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000, 0 0 12px rgba(59,130,246,0.6)'
            }}>
              Friends Profile
            </p>
          )}
          <img src={profileImageSrc} alt={username} style={{
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            objectFit: 'cover',
            margin: '0 auto 16px',
            border: `3px solid ${darkMode ? '#ffffff' : '#000000'}`,
            boxShadow: '0 0 0 3px rgba(0,0,0,0.5), 0 0 18px rgba(59,130,246,0.45)'
          }} />
          {isOwnProfile && (
            <div style={{ marginBottom: '16px' }}>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
              <button
                onClick={() => { playButtonClick(); fileInputRef.current && fileInputRef.current.click(); }}
                disabled={uploadingImage}
                style={{
                  padding: '8px 12px',
                  backgroundColor: uploadingImage ? '#9ca3af' : '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: uploadingImage ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                }}
              >
                {uploadingImage ? 'Uploading...' : 'Change Photo'}
              </button>
            </div>
          )}
          <h1 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: darkMode ? '#ffffff' : '#1f2937',
            marginBottom: '8px',
            textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
            filter: 'drop-shadow(-1px -1px 0 #000) drop-shadow(1px -1px 0 #000) drop-shadow(-1px 1px 0 #000) drop-shadow(1px 1px 0 #000)'
          }}>
            {username}
          </h1>
          {isOwnProfile && (
            <p style={{
              fontSize: '14px',
              color: '#f9fafb',
              marginBottom: '10px',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              {user?.email || profile?.email}
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '2px 10px',
              borderRadius: '999px',
              background: 'linear-gradient(90deg, rgba(0,0,0,0.5), rgba(0,0,0,0.1))',
              boxShadow: '0 0 10px rgba(0,0,0,0.3)',
              display: 'inline-flex',
              gap: '6px'
            }}>
              <span style={{ color: '#ffffff', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Rank:</span>
              <span style={{
                color: 'transparent',
                backgroundImage: `linear-gradient(180deg, #ffffff 0%, #ffffff 75%, ${beltRank.color} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold'
              }}>
                {beltRank.name}
              </span>
            </div>
            <div style={{
              width: '160px',
              height: '12px',
              borderRadius: '8px',
              backgroundColor: beltRank.color,
              backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 6px, rgba(255,255,255,0) 6px 12px)',
              border: beltRank.name === 'White Belt' ? '2px solid #000000' : '2px solid #ffffff',
              position: 'relative',
              boxShadow: '0 0 12px rgba(0,0,0,0.35), 0 0 16px rgba(59,130,246,0.35)'
            }}>
              <span style={{
                position: 'absolute',
                right: '10px',
                top: '0',
                bottom: '0',
                width: '18px',
                backgroundColor: beltRank.stripe,
                borderLeft: '2px solid #000000',
                boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)'
              }} />
              <span style={{
                position: 'absolute',
                left: '8px',
                top: '50%',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                transform: 'translateY(-50%)',
                background: 'radial-gradient(circle, rgba(255,255,255,0.9), rgba(255,255,255,0.0))',
                opacity: 0.8
              }} />
            </div>
            {isOwnProfile && (
              <button
                onClick={() => { playButtonClick(); setShowRanksModal(true); }}
                style={{
                  marginTop: '6px',
                  padding: '6px 12px',
                  backgroundColor: '#0ea5e9',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                }}
              >
                🏆 Ranks
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
            <p style={{
              minHeight: '42px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={getBeltGradientText(beltRank.color, '30px')}>
                {loading ? <Spinner color="#3b82f6" /> : profileSessions}
              </span>
            </p>
            <p style={{ fontSize: '12px', color: '#f9fafb', marginBottom: '4px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
              Sessions
            </p>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#f87171', minHeight: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000', filter: 'drop-shadow(-1px -1px 0 #000) drop-shadow(1px -1px 0 #000) drop-shadow(-1px 1px 0 #000) drop-shadow(1px 1px 0 #000)' }}>
              {loading ? <Spinner color="#a855f7" /> : profileWorkouts}
            </p>
            <p style={{ fontSize: '10px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
              Workouts
            </p>
          </div>

          {/* Notification Toggle - Friend Profiles Only */}
          {!isOwnProfile && profile && (
            <div style={{
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '16px'
              }}>
                <p style={{
                  fontWeight: 'bold',
                  fontSize: '15px',
                  color: darkMode ? '#f1f5f9' : '#1e293b',
                  margin: 0,
                  textAlign: 'center',
                  textShadow: darkMode ? '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' : 'none'
                }}>
                  🔔 Session Notifications
                </p>
                <button
                  onClick={toggleNotifications}
                  disabled={loadingNotifPref}
                  style={{
                    position: 'relative',
                    width: '52px',
                    height: '28px',
                    borderRadius: '14px',
                    border: 'none',
                    cursor: loadingNotifPref ? 'not-allowed' : 'pointer',
                    backgroundColor: notificationsEnabled ? '#22c55e' : '#94a3b8',
                    transition: 'background-color 0.3s',
                    opacity: loadingNotifPref ? 0.6 : 1
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    left: notificationsEnabled ? '26px' : '2px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#ffffff',
                    transition: 'left 0.3s',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                  }} />
                </button>
              </div>

              {/* Test Notification Button */}
              {notificationsEnabled && (
                <button
                  onClick={async () => {
                    setSendingTestNotif(true);
                    try {
                      const permission = Notification.permission === 'granted'
                        ? 'granted'
                        : await Notification.requestPermission();

                      if (permission !== 'granted') {
                        alert('Notifications are blocked. Please allow notifications in Chrome settings for this site.');
                        return;
                      }
                      // Add timeout to prevent hanging
                      const timeoutPromise = new Promise((_, reject) => 
                        setTimeout(() => reject(new Error('Timeout - notification took too long')), 5000)
                      );
                      
                      const notificationPromise = (async () => {
                        // Try service worker first
                        if ('serviceWorker' in navigator) {
                          let registration = await navigator.serviceWorker.getRegistration();
                          if (!registration) {
                            registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                          }
                          await registration.update();
                          await registration.showNotification('🏋️ FitBudly Test', {
                            body: 'Notifications are working! You will receive updates when your friends log sessions.',
                            icon: '/logo192.png',
                            badge: '/logo192.png',
                          });
                        } else {
                          // Fallback to regular Notification API
                          new Notification('🏋️ FitBudly Test', {
                            body: 'Notifications are working! You will receive updates when your friends log sessions.',
                            icon: '/logo192.png',
                            badge: '/logo192.png'
                          });
                        }
                      })();
                      
                      await Promise.race([notificationPromise, timeoutPromise]);
                      
                      alert('Test notification sent! Check if it appeared.');
                    } catch (err) {
                      console.error('Test notification error:', err);
                      alert('Failed: ' + err.message);
                    } finally {
                      setSendingTestNotif(false);
                    }
                  }}
                  disabled={sendingTestNotif}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: sendingTestNotif ? 'not-allowed' : 'pointer',
                    marginBottom: '12px',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  {sendingTestNotif ? 'Sending...' : '🔔 Send Test Notification'}
                </button>
              )}

              {/* Remove Friend Button */}
              <button
                onClick={() => { playButtonClick(); setRemoveFriendError(''); setShowRemoveFriendModal(true); }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                }}
              >
                Remove Friend
              </button>
            </div>
          )}
        </div>


        {/* Delete Profile Button (Own Profile) */}
        {isOwnProfile && (
          <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => { playButtonClick(); setShowDeleteModal(true); }}
              style={{
                width: 'fit-content',
                padding: '10px 16px',
                backgroundColor: '#ef4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              🗑️ Delete Profile
            </button>
          </div>
        )}

        {/* Session History */}
        <div>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: darkMode ? '#ffffff' : '#1f2937',
            marginBottom: '16px',
            textAlign: 'center',
            textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
          }}>
            Session History
          </h2>
          {loadingSessions ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <Spinner color="#3b82f6" />
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(sessions)
              .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
              .map(([date, session], index) => {
                const sessionNumber = Object.keys(sessions).length > 0 ? sessionCount - index : 0;
                const sessionBelt = getBeltForSessions(sessionNumber);
                return (
                  <div key={date} style={{ marginBottom: '8px' }}>
                    {/* Date Frame */}
                    <div style={{
                      backgroundColor: '#3b82f6',
                      color: '#ffffff',
                      padding: '8px 16px',
                      borderRadius: '8px 8px 0 0',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: 'bold',
                      textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                    }}>
                      {date}
                    </div>
                    {/* Session Card */}
                    <div style={{
                      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                      padding: '16px',
                      borderRadius: '0 0 8px 8px',
                      border: '2px solid ' + (darkMode ? '#374151' : '#e5e7eb'),
                      borderTop: 'none',
                      display: 'grid',
                      gridTemplateColumns: isOwnProfile ? '1fr 2fr 1fr 1fr' : '1fr 2fr 1fr',
                      alignItems: 'center',
                      gap: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        ...getBeltGradientText(sessionBelt.color, '20px')
                      }}>
                        #{sessionNumber}
                      </div>
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        justifyContent: 'center'
                      }}>
                        {session.workouts?.map((workout, idx) => (
                          <div key={idx} style={{
                            backgroundColor: '#3b82f6',
                            color: '#ffffff',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '2px',
                            textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                          }}>
                            <div>{workout.name}</div>
                            <div>🔥{workout.intensity}/10</div>
                          </div>
                        ))}
                      </div>
                      {!isOwnProfile && (
                        <p style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#f9fafb',
                          margin: 0,
                          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                        }}>
                          {Object.keys(sessions).length > 0 ? sessionCount - index : 0} workouts
                        </p>
                      )}
                      {isOwnProfile && (
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => startEditSession(date)}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: '#8b5e3c',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteSession(date)}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: '#ef4444',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            {Object.keys(sessions).length === 0 && (
              <p style={{
                textAlign: 'center',
                color: '#f9fafb',
                paddingTop: '32px',
                paddingBottom: '32px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                No sessions yet
              </p>
            )}
          </div>
          )}
        </div>

        {showCropModal && (
          <div onClick={() => setShowCropModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1000 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#1f2937', border: '2px solid #374151', borderRadius: '16px', padding: '20px', width: '100%', maxWidth: '420px', textAlign: 'center' }}>
              <h3 style={{ color: '#f9fafb', marginBottom: '12px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Crop your photo</h3>
              <div style={{ position: 'relative', width: '100%', height: '260px', background: '#111827', borderRadius: '12px', overflow: 'hidden' }}>
                <Cropper
                  image={cropImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                />
              </div>
              <div style={{ marginTop: '16px' }}>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
                <button
                  onClick={() => { playButtonClick(); setShowCropModal(false); }}
                  style={{ padding: '10px 16px', backgroundColor: '#4b5563', color: '#ffffff', borderRadius: '8px', fontWeight: 'bold', border: 'none', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => { playButtonClick(); applyCrop(); }}
                  style={{ padding: '10px 16px', backgroundColor: '#3b82f6', color: '#ffffff', borderRadius: '8px', fontWeight: 'bold', border: 'none', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
                >
                  Use Photo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div onClick={() => { playButtonClick(); setShowEditModal(false); setEditingSession(null); }} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              width: '90%',
              maxWidth: '420px',
              backgroundColor: darkMode ? '#1f2937' : '#ffffff',
              border: '2px solid ' + (darkMode ? '#374151' : '#e5e7eb'),
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
              position: 'relative'
            }}>
              <button
                onClick={() => { playButtonClick(); setShowEditModal(false); setEditingSession(null); }}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'none',
                  border: 'none',
                  color: darkMode ? '#ffffff' : '#1f2937',
                  fontSize: '20px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
              <h3 style={{
                fontSize: '22px',
                fontWeight: 'bold',
                color: darkMode ? '#ffffff' : '#1f2937',
                marginBottom: '12px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                Edit Workouts
              </h3>
              <p style={{ color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '16px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                Toggle to add or remove.
                <br />
                Selected appear in red.
              </p>

              {/* Selected (red) with intensity sliders */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px'
              }}>
                {editWorkouts.map(name => (
                  <div key={name} style={{
                    backgroundColor: darkMode ? '#1f2937' : '#f3f4f6',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '2px solid #ef4444'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: darkMode ? '#ffffff' : '#1f2937',
                        textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                      }}>
                        {name}
                      </span>
                      <button
                        onClick={() => toggleEditWorkout(name)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#ef4444',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                        Intensity:
                      </span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={editIntensities[name] || 5}
                        onChange={(e) => setEditIntensities(prev => ({ ...prev, [name]: parseInt(e.target.value) }))}
                        style={{ flex: 1 }}
                      />
                      <span style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#3b82f6',
                        minWidth: '30px',
                        textAlign: 'center',
                        textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                      }}>
                        🔥{editIntensities[name] || 5}
                      </span>
                    </div>
                  </div>
                ))}
                {editWorkouts.length === 0 && (
                  <span style={{ color: darkMode ? '#9ca3af' : '#6b7280', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>No workouts selected</span>
                )}
              </div>

              {/* Available (native colors) */}
              <div style={{
                backgroundColor: darkMode ? '#111827' : '#f3f4f6',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid ' + (darkMode ? '#374151' : '#e5e7eb')
              }}>
                <p style={{ fontWeight: 'bold', color: darkMode ? '#ffffff' : '#1f2937', marginBottom: '8px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Available</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                  {['Gym/Pull', 'Gym/Push', 'Gym/Legs', 'Gym/Full Body', 'Gym/Upper Body', 'BJJ', 'Basketball', 'Swimming'].map(name => {
                    const isGymPreset = name.startsWith('Gym/');
                    const hasGymSelected = editWorkouts.some(w => w.startsWith('Gym/'));
                    const isDisabled = editWorkouts.includes(name) ? false : (isGymPreset && hasGymSelected);
                    return (
                      <button key={name}
                        onClick={() => !isDisabled && toggleEditWorkout(name)}
                        disabled={isDisabled}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '12px',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          border: 'none',
                          cursor: editWorkouts.includes(name) || isDisabled ? (editWorkouts.includes(name) ? 'pointer' : 'not-allowed') : 'pointer',
                          backgroundColor: editWorkouts.includes(name) ? '#ef4444' : isDisabled ? '#9ca3af' : '#3b82f6',
                          color: editWorkouts.includes(name) || isDisabled ? '#ffffff' : '#ffffff',
                          opacity: isDisabled ? 0.5 : 1,
                          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                        }}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexDirection: 'column' }}>
                <button
                  onClick={() => { playButtonClick(); setShowEditModal(false); }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: darkMode ? '#374151' : '#e5e7eb',
                    color: darkMode ? '#ffffff' : '#1f2937',
                    fontWeight: 'bold',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => { playButtonClick(); saveSessionEdits(); }}
                  disabled={!hasChanges()}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: hasChanges() ? '#22c55e' : '#9ca3af',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: hasChanges() ? 'pointer' : 'not-allowed',
                    fontSize: '16px',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
                    opacity: hasChanges() ? 1 : 0.6
                  }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image picker modal removed; direct file dialog used */}

        {showDeleteModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: darkMode ? '#1f2937' : '#ffffff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '400px',
              width: '100%',
              position: 'relative'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                  setDeleteError('');
                }}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  padding: '0',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#ef4444',
                marginBottom: '16px',
                textAlign: 'center',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                Delete Profile
              </h2>
              <p style={{ color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '16px', fontSize: '14px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                This will permanently delete your profile and all your data. Type "delete profile" below to confirm:
              </p>
              <input
                type="text"
                placeholder='Type "delete profile"'
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  marginBottom: '16px',
                  textAlign: 'center',
                  ...(darkMode && { backgroundColor: '#374151', borderColor: '#4b5563', color: '#ffffff' })
                }}
              />
              {deleteError && (
                <p style={{ color: '#ef4444', marginBottom: '16px', fontSize: '14px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                  {deleteError}
                </p>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleDeleteProfile}
                  disabled={isDeletingProfile}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: isDeletingProfile ? '#9ca3af' : '#22c55e',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: isDeletingProfile ? 'not-allowed' : 'pointer',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  {isDeletingProfile ? 'Deleting...' : 'Yes'}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                    setDeleteError('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {showRemoveFriendModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: darkMode ? '#1f2937' : '#ffffff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '400px',
              width: '100%',
              position: 'relative'
            }}>
              <button
                onClick={() => { setShowRemoveFriendModal(false); setRemoveFriendError(''); }}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  padding: '0',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#ef4444',
                marginBottom: '16px',
                textAlign: 'center',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                Remove Friend
              </h2>
              <p style={{ color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '16px', fontSize: '14px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                Are you sure you want to remove this friend?
              </p>
              {removeFriendError && (
                <p style={{ color: '#ef4444', marginBottom: '16px', fontSize: '14px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                  {removeFriendError}
                </p>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={async () => {
                    try {
                      setRemoveFriendError('');
                      await removeFriend(profile.uid);
                      setShowRemoveFriendModal(false);
                      // Navigate back to main page after successful deletion
                      if (onBackToMain) {
                        onBackToMain();
                      }
                    } catch (err) {
                      setRemoveFriendError(err.message || 'Failed to remove friend');
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: '#22c55e',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  Yes
                </button>
                <button
                  onClick={() => { setShowRemoveFriendModal(false); setRemoveFriendError(''); }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteSessionModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: darkMode ? '#1f2937' : '#ffffff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '400px',
              width: '100%',
              position: 'relative'
            }}>
              <button
                onClick={() => {
                  setShowDeleteSessionModal(false);
                  setSessionToDelete(null);
                }}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  padding: '0',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#ef4444',
                marginBottom: '16px',
                textAlign: 'center',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                Delete Session
              </h2>
              <p style={{ color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '24px', fontSize: '14px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                Are you sure you want to delete this session?
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={confirmDeleteSession}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: '#22c55e',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  Yes
                </button>
                <button
                  onClick={() => {
                    setShowDeleteSessionModal(false);
                    setSessionToDelete(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {showRanksModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1f2937',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '420px',
              width: '100%'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h2 style={{ color: '#f9fafb', fontWeight: 'bold', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Belt Ranks</h2>
                <button
                  onClick={() => setShowRanksModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#f9fafb' }}
                >
                  ✖
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {BELT_RANKS.map((belt) => {
                  const unlocked = sessionCount >= belt.threshold;
                  return (
                    <div key={belt.name} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: '2px solid #374151',
                      backgroundColor: '#111827'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '80px',
                          height: '10px',
                          borderRadius: '6px',
                          backgroundColor: belt.color,
                          border: '2px solid #000000',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            right: '6px',
                            top: '0',
                            bottom: '0',
                            width: '12px',
                            backgroundColor: belt.stripe,
                            borderLeft: '2px solid #000000'
                          }} />
                        </div>
                        <div>
                          <div style={{ color: '#f9fafb', fontWeight: 'bold' }}>{belt.name}</div>
                          <div style={{ color: '#9ca3af', fontSize: '12px' }}>{belt.threshold}+ sessions</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '18px' }}>{unlocked ? '✅' : '🔒'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}